import type { ResearchRoomMessage } from '@repo/contracts';

/**
 * How sure Jev has to be that a follow-up would get somewhere before it is
 * offered on the card. A button that leads nowhere teaches the reader to
 * ignore the next one, so an even read offers nothing.
 *
 * Challenge's bar is higher: on real answers Jev reads 0.6–0.75 for almost
 * anything ("someone could see it differently"), and 0.9+ for a claim the
 * room would actually argue with — which is the one worth the button.
 */
const OFFER_AT = { challenge: 0.8, why: 0.6 } as const;

export interface FollowUpPicks {
  /** The round whose answers can still be followed up; older ones cannot. */
  round: number | null;
  /** The one answer to offer "Why?" on, if any earns it. */
  why: string | null;
  /** The one answer to offer "Challenge" on, if any earns it. */
  challenge: string | null;
}

/**
 * Where a follow-up is worth offering, instead of under every answer — two
 * buttons on every card stop being seen at all.
 *
 * The judgment is Jev's, read beside each answer's reactions
 * (`followUpOdds`): would "Why?" surface a reason the answer left out, and
 * would the others see it differently if challenged. The policy is here:
 * only the latest round, the one answer it is likeliest for, and only past
 * its `OFFER_AT`. Never "Why?" on a round that was itself a "why?".
 *
 * Every other answer gets neither — not even on hover.
 */
export function pickFollowUps(messages: ResearchRoomMessage[]): FollowUpPicks {
  const answers = messages.filter(
    (m) => m.kind === 'answer' && m.status === 'done' && m.text,
  );
  const round = answers.reduce<number | null>(
    (latest, m) => (latest === null || m.round > latest ? m.round : latest),
    null,
  );
  if (round === null) return { challenge: null, round, why: null };

  const latest = answers.filter((m) => m.round === round);
  const question = messages.find(
    (m) => m.round === round && (m.kind === 'question' || m.kind === 'poll'),
  );

  const likeliest = (mode: 'challenge' | 'why') =>
    latest
      .map((m) => ({ id: m.id, p: m.followUpOdds?.[mode] ?? 0 }))
      .filter((m) => m.p >= OFFER_AT[mode])
      .sort((a, b) => b.p - a.p)[0]?.id ?? null;

  return {
    challenge: likeliest('challenge'),
    round,
    why: question?.about?.mode === 'why' ? null : likeliest('why'),
  };
}
